/* global Element */

import angular from 'angular'
import clone from 'lodash/clone'
import defaultsDeep from 'lodash/defaultsDeep'
import isArray from 'lodash/isArray'
import Tether from 'tether'

import { observeResize, supportsPassiveEvents } from './vd-popover.util'

export const POPOVER_TYPES = {
  TOOLTIP: 'tooltip',
  POPOVER: 'popover',
}

const DEFAULT_OPTIONS = {
  controller: 'PopoverDefaultController',
  controllerAs: '$ctrl',
  direction: 'below',
  type: POPOVER_TYPES.POPOVER,
}

const DEFAULT_TETHER_CONFIG = {
  enabled: false,
  classPrefix: 'vd-popover-tether',
}

const DEFAULT_CONSTRAINTS = {
  to: 'window',
  attachment: 'together',
}

const DIRECTION_CONFIG = {
  // general directions
  below: {
    attachment: 'top left',
    targetAttachment: 'bottom left',
  },
  center: {
    attachment: 'top center',
    targetAttachment: 'bottom center',
  },
  left: {
    attachment: 'middle right',
    targetAttachment: 'middle left',
  },
  right: {
    attachment: 'middle left',
    targetAttachment: 'middle right',
  },

  // specific directions
  bottomLeft: {
    attachment: 'top left',
    targetAttachment: 'bottom left',
  },
  bottomRight: {
    attachment: 'top right',
    targetAttachment: 'bottom right',
  },
  topLeft: {
    attachment: 'bottom left',
    targetAttachment: 'top left',
  },
  topRight: {
    attachment: 'bottom right',
    targetAttachment: 'top right',
  },
}

const POPOVER_EVENTS = ['click', 'touchstart']
const NV_SIDENAV = '.nv-sidenav'

/**
 * Service for opening and closing popovers.
 *
 * @class PopoverService
 */
export default class PopoverService {
  constructor(
    $q,
    $document,
    ModalService,
    Modal,
    vdModalsContainerService,
    vdWindowDelegate,
    vdEscapeStack
  ) {
    'ngInject'
    this._$q = $q
    this._$document = $document
    this._ModalService = ModalService
    this._Modal = Modal
    this._vdModalsContainerService = vdModalsContainerService
    this._vdWindowDelegate = vdWindowDelegate
    this._vdEscapeStack = vdEscapeStack

    this._popover = null
    this._openingPopovers = []

    this._closePopover = () => this.close()

    this._supportsPassiveEvents = supportsPassiveEvents()

    // Handle viewportChange, not resize, because the Android keyboard triggers
    // a resize event. If we close on resize, it's impossible to type into an
    // input in a popover on Android.
    vdWindowDelegate.on(vdWindowDelegate.EVENTS.viewportChange, () =>
      this._closePopover()
    )
  }

  // ============================== Public API =============================== //

  /**
   * Open a popover with the given content. Only one popover can be open at a time so if another popover is already
   * open it will be closed before this one is opened.
   *
   * @method open
   *
   * @param  {Object} options
   *         All the same options supported by the Modal service are supported (template, templateUrl, controller, etc).
   *
   *         @param {String|jQuery|HTMLElement} trigger
   *                The element that triggered the popover - used to align the popover and beak
   *
   *         @param {String} [direction = 'below']
   *                The direction to open the popover, supported values are 'below' (below and to the left), 'left'
   *                (left middle), 'center' (below and centered), 'bottomLeft' (same as 'below'), 'bottomRight' (below
   *                and to the right), 'topLeft' (above and to the left) and 'topRight' (above and to the right).
   *
   *         @param {String|ViewportData} [openAsModalAtViewport]
   *                The VD_VIEWPORT (name or range instance) at which to open as a modal instead of a popover
   *
   *         @param {Boolean|String} [inheritWidthFromTrigger='min-width']
   *                By default the popover will have a minimum width of the trigger element's width. Set this to false
   *                to allow the popover to size completely independently or set this to "width" or "max-width" to set
   *                the width or max-width respectively to the width of the trigger element. Defaults to "min-width".
   *
   *         @param {Boolean} [toggleOnTriggerClick]
   *                By default clicking the trigger element will close the popover making the trigger element behave
   *                as a toggle. Set this to false to keep the popover open when the trigger element is clicked.
   *                Defaults to true.
   *
   *         @param {Array} [constraints]
   *                Additional tether constraints to apply to the popover (defaults constrain popover to the window and
   *                vd-main-content container). See https://tether.io for more information.
   *
   *         @param {Boolean} [pin]
   *                Whether to pin the popover to the edge of the window should it not fit. If set to false
   *                the popover will overflow past the edge of the window. Defaults to true.
   *
   *         @param {Boolean} [repositionOnPopoverResize]
   *                Whether to reposition the popover if the content inside the popover resizes. Defaults to true.
   *
   * @return {Promise<Popover>} A promise resolved with the popover instance once the popover is open. The popover
   *         instance object extends the angular-modal-service instance and so provides `close`, `closed` promises etc.
   */
  open(options) {
    if (this._popover && options.trigger === this._popover.options.trigger) {
      return this._$q.resolve(this._popover.modal)
    }

    return this._queueOpen(() =>
      this.close()
        .then(() => {
          const domElement =
            typeof options.trigger === 'string'
              ? this._$document[0].querySelector(options.trigger)
              : options.trigger
          const triggerElement = angular.element(domElement)
          const modalsContainer = this._vdModalsContainerService.getModalsContainerElement()
          const triggeredInsideSideNav = Boolean(
            triggerElement[0].closest(NV_SIDENAV)
          )
          const triggeredInsideModal =
            Boolean(modalsContainer[0].contains(triggerElement[0])) ||
            triggeredInsideSideNav

          const { openAsModalAtViewport } = options
          if (
            !triggeredInsideModal &&
            openAsModalAtViewport &&
            this._vdWindowDelegate.compareToViewport(openAsModalAtViewport) <= 0
          ) {
            return this._openAsModal(options)
          } else {
            return this._openAsPopover(options, {
              triggerElement,
              triggeredInsideModal,
            })
          }
        })
        .then(popoverInstance => {
          this._registerPopoverInstance(popoverInstance)
          const popoverController = popoverInstance.element.controller(
            'vdPopover'
          )
          if (popoverController) {
            // We need to let the popover controller know that it's been registered so that it can
            // initialize itself and add appropriate classes.
            popoverController.onPopoverRegistered()
          }
          return popoverInstance.modal
        })
    )
  }

  /**
   * Close the popover if there is one open.
   *
   * @method close
   *
   * @param  {Object} [options]
   *
   *         @param {Selector|Element|jQuery} [options.match]
   *                If specified the open popover will only be closed if the popover element matches the given value
   *                (using Element.matches or element equality).
   *                For example, vdPopover.close({ match: '.vd-popover-container--inside-modal' })
   *                will only close the open popover if it was opened inside a modal.
   *
   * @return {Promise} A promise resolved once the popover has been closed
   */
  close(options = {}) {
    return this._$q(resolve => {
      if (!this.isOpen(options)) {
        return resolve()
      }

      if (!this._popover.closing) {
        const closingDeferred = this._$q.defer()
        this._popover.closing = closingDeferred.promise

        this._popover.modal.closeModal()
        this._popover.modal.closed.finally(() => closingDeferred.resolve())
      }

      this._popover.closing.then(resolve)
    })
  }

  /**
   * Check if a popover is open. If a match is passed it will check that any open popover matches the given selector.
   *
   * @method isOpen
   *
   * @param  {Object} [options]
   *
   *         @param {Selector|Element|jQuery} [options.match]
   *                If specified this method will only return true if the open popover element matches the given value
   *                (using Element.matches or element equality).
   *                For example, vdPopover.isOpen({ match: '.vd-popover-container--inside-modal' })
   *                only returns true if the open popover was opened inside a modal.
   *
   * @return true if there is a popover open [that matches the specified match]
   */
  isOpen(options = {}) {
    if (!this._popover) {
      return false
    }

    if (options.match) {
      if (typeof options.match === 'string') {
        return this._popover.element[0].matches(options.match)
      }
      const match =
        options.match instanceof Element ? options.match : options.match[0]
      if (this._popover.element[0] !== match) {
        return false
      }
    }

    return true
  }

  // ============================== Protected API =============================== //

  /**
   * Get the popover instance based on the given vd-popover element.
   *
   * @protected
   * @method _getPopoverInstance
   *
   * @param  {jQuery} vdPopoverElement
   *         The $element instance of the vd-popover element
   *
   * @return {PopoverInstance} Data on the related popover instance including `modal`, `options`, `element`, etc
   */
  _getPopoverInstance(vdPopoverElement) {
    if (this._popover && this._popover.element[0] === vdPopoverElement[0]) {
      return this._popover
    }

    return null
  }

  // ============================== Private Methods =============================== //

  /**
   * Queue the opening of a popover behind any other popovers still in the process of being opened. This ensures we
   * only ever have one open at a time, even if open is somehow called more than once before the first popover closes.
   *
   * @private
   * @method _queueOpen
   *
   * @param  {Function} openPopover
   *         The function that will open the popover and return the promise that resolves to the open popover
   *
   * @return {Promise} The resulting promise that resolves once this specific popover is opened
   */
  _queueOpen(openPopover) {
    let promise

    if (!this._openingPopovers.length) {
      promise = openPopover()
    } else {
      const lastIndex = this._openingPopovers.length - 1
      promise = this._openingPopovers[lastIndex].then(() => openPopover())
    }

    promise.then(popover => this._openingPopovers.shift())
    this._openingPopovers.push(promise)

    return promise
  }

  /**
   * Open the specified popover content as a standard popover.
   *
   * @private
   * @method _openAsPopover
   *
   * @param  {Object} options
   *         The options passed to vdPopover.open()
   *
   * @param  {Object} openingContext
   *         Additional info on the context the popover is being opened in (`triggerElement`, `triggeredInsideModal`)
   *
   * @return {Promise} A promise resolved with the popover instance once the popover is open
   */
  _openAsPopover(options, openingContext) {
    const config = clone(options)

    defaultsDeep(config, DEFAULT_OPTIONS)

    // This is done for backwards compatibility and consistency with our Modal service API. For some reason we deviate
    // the structure of our modal config from `ModalService`, i.e. having `config.input` options directly on the
    // `config` object.
    config.inputs = { options }

    return this._ModalService.showModal(config).then(modal => {
      const eventOptions = this._getEventOptions()
      const popoverInstance = this._createPopoverInstance(modal, options)
      const element = popoverInstance.element
      const elementNode = element[0]
      const { triggerElement, triggeredInsideModal } = openingContext
      const triggerElementNode = triggerElement[0]

      const { pin = true } = config
      const defaultConstraints = defaultsDeep({ pin }, DEFAULT_CONSTRAINTS)

      let bodyElement
      if (triggeredInsideModal && config.type !== POPOVER_TYPES.TOOLTIP) {
        const containingDialog = angular.element(
          triggerElement[0].closest('.vd-dialog-container')
        )
        if (containingDialog.length) {
          // If triggered inside a modal - we attach the popover to the DOM inside that modal to ensure you can still
          // focus fields inside the popover (as modals have a focus trap)
          containingDialog.append(
            '<div class="vd-dialog-popover-container"></div>'
          )
          const containerEl = angular.element(
            containingDialog[0].lastElementChild
          )

          bodyElement = containerEl[0]
          modal.closed.finally(() => containerEl.remove())
        }
      }

      const tetherConfig = defaultsDeep(
        {
          element: elementNode,
          target: triggerElementNode,
          constraints: [defaultConstraints],
          bodyElement,
        },
        DIRECTION_CONFIG[config.direction],
        DEFAULT_TETHER_CONFIG
      )

      const vdMainContent = this._$document[0].querySelector('.vd-main-content')
      if (vdMainContent && vdMainContent.contains(triggerElementNode)) {
        tetherConfig.constraints.unshift({
          to: vdMainContent,
          attachment: 'together',
          pin,
        })
      }

      if (config.constraints) {
        if (isArray(config.constraints)) {
          tetherConfig.constraints.unshift(...config.constraints)
        } else {
          tetherConfig.constraints.unshift(config.constraints)
        }
      }

      if (
        config.direction === 'below' &&
        config.inheritWidthFromTrigger !== false &&
        // @todo This is here for backwards compatibility, we should clean it up at some point
        config.inheritMinWidthFromTrigger !== false
      ) {
        const property =
          typeof config.inheritWidthFromTrigger === 'string'
            ? config.inheritWidthFromTrigger
            : 'min-width'
        element.css(property, `${triggerElement[0].offsetWidth}px`)
      }

      if (triggeredInsideModal) {
        element.addClass('vd-popover-container--inside-modal')
      }

      const tether = new Tether(tetherConfig)
      popoverInstance.tether = tether

      modal.scope.$applyAsync(() => {
        tether.enable()

        // Respond to document click and touchstart events
        this.onDocumentElementInteraction = event => {
          const isPopoverClick =
            event.target === elementNode || elementNode.contains(event.target)
          const isTriggerClick = event.target === triggerElementNode

          if (
            isPopoverClick ||
            (config.toggleOnTriggerClick === false && isTriggerClick)
          ) {
            return
          }

          this.close()
        }

        POPOVER_EVENTS.forEach(event => {
          this._$document[0].addEventListener(
            event,
            this.onDocumentElementInteraction,
            eventOptions
          )
        })

        if (config.repositionOnPopoverResize !== false) {
          popoverInstance.resizeHandler = observeResize(elementNode, () => {
            tether.position()
          })
        }

        this._vdEscapeStack.register(this._closePopover)
      })

      return popoverInstance
    })
  }

  /**
   * Helper to set passive event listener options (if supported).
   *
   * @private
   * @method _getEventOptions
   *
   * @returns {Object|Boolean} The appropriate options for the event listener.
   */
  _getEventOptions() {
    return this._supportsPassiveEvents ? { passive: true } : false
  }

  /**
   * Open the specified popover content as a modal.
   *
   * @private
   * @method _openAsModal
   *
   * @param  {Object} options
   *         The options passed to vdPopover.open()
   *
   * @return {Promise} A promise resolved with the popover instance once the popover modal is open
   */
  _openAsModal(options) {
    const config = clone(options)

    defaultsDeep(config, DEFAULT_OPTIONS)

    return this._Modal(config).then(modal => {
      const popoverInstance = this._createPopoverInstance(modal, options)
      popoverInstance.openAsModal = true
      return popoverInstance
    })
  }

  /**
   * Create a popover instance object based on the modal instance and supplied options.
   *
   * @private
   * @method _createPopoverInstance
   *
   * @param  {Modal} modal
   *         The instance of the modal returned by the angular-modal-service
   *
   * @param  {Object} options
   *         The options passed to vdPopover.open()
   *
   * @return {PopoverInstance} Data on the popover instance including `modal`, `options`, `element`, etc
   */
  _createPopoverInstance(modal, options) {
    const popoverSelector = 'vd-popover, vd-tooltip'
    const element = modal.element[0].matches(popoverSelector)
      ? modal.element
      : angular.element(modal.element[0].querySelector(popoverSelector))

    if (options.hasPopoverList) {
      element.addClass('vd-popover--with-list')
    }

    return { modal, options, element }
  }

  /**
   * Register the given popover instance data as the current active popover. Also binds cleanup handling for when the
   * popover is closed.
   *
   * @private
   * @method _registerPopoverInstance
   *
   * @param  {PopoverInstance} popoverInstance
   *         The popover instance to register as active
   */
  _registerPopoverInstance(popoverInstance) {
    this._popover = popoverInstance
    const eventOptions = this._getEventOptions()

    const { modal, tether, resizeHandler } = popoverInstance
    modal.closed.finally(() => {
      if (tether) {
        tether.destroy()
      }

      POPOVER_EVENTS.forEach(event => {
        this._$document[0].removeEventListener(
          event,
          this.onDocumentElementInteraction,
          eventOptions
        )
      })

      if (resizeHandler) {
        resizeHandler.remove()
      }

      this._vdEscapeStack.deregister(this._closePopover)

      this._popover = null
    })
  }
}
